import { Button, Card, Empty, Input, Space, Spin, Tag, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { ZhMap, type ShipInfoVo, type WharfEntity as ZhWharfEntity, type ZhMapRef } from "zhihao-ui-react";

import { FindLocationDetailPage } from "./FindLocationDetailPage";
import {
    loadFindPort,
    loadFindShipArea,
    loadFindShipDetail,
    loadFindShipExtent,
    loadFindShips,
    loadFindWharf,
    normalizeFindShipRow,
    parseFindLocationCoordinates,
    toFindLocationGeoPointList
} from "./data";
import type {
    FindLocationPort,
    FindLocationPortLoader,
    FindLocationWharfSelection,
    FindShipAreaLoader,
    FindShipDetailLoader,
    FindShipExtentLoader,
    FindShipLoader,
    FindShipRow
} from "./types";

export interface FindLocationPageProps {
    permissions?: string[];
    loadShips?: FindShipLoader;
    loadShip?: FindShipDetailLoader;
    loadExtentShips?: FindShipExtentLoader;
    loadAreaShips?: FindShipAreaLoader;
    loadPort?: FindLocationPortLoader;
    loadWharf?: FindLocationPortLoader;
}

// 沿用 Vue 查船页的默认地图中心与缩放级别，并通过 ZhMapRef 接入业务点位图层。
const DEFAULT_MAP_CENTER: [number, number] = [114.84, 30.52];
const DEFAULT_MAP_ZOOM = 13;
// 查船页面沿用 Vue 的主入口、定制搜船和关注船权限，避免子权限用户被 React 入口拦截。
const FIND_LOCATION_ACCESS_PERMISSIONS = ["ROLE_LF_SHIP", "ROLE_LF_SHIP_CUSTOMIZE", "ROLE_LF_SHIP_LIKE"];

/** 查船入口；权限显式传入时沿用 Vue 查船相关权限的任一命中规则。 */
export function FindLocationPage({
    permissions,
    loadShips = loadFindShips,
    loadShip = loadFindShipDetail,
    loadExtentShips = loadFindShipExtent,
    loadAreaShips = loadFindShipArea,
    loadPort = loadFindPort,
    loadWharf = loadFindWharf
}: FindLocationPageProps) {
    const { mmsi } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    // 测试直挂组件或路由尚未串行接入时，从 Vue 兼容路径读取参数。
    const routeMmsi = mmsi || location.pathname.match(/\/findLocation\/([^/]+)$/)?.[1];
    const canSearch =
        permissions === undefined ||
        permissions.some(permission => FIND_LOCATION_ACCESS_PERMISSIONS.includes(permission));
    const [keyword, setKeyword] = useState("");
    const [result, setResult] = useState<FindShipRow[]>([]);
    const [lastKeyword, setLastKeyword] = useState("");
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedMapShip, setSelectedMapShip] = useState("");
    const [selectedMapPort, setSelectedMapPort] = useState<FindLocationPort | null>(null);
    const [selectedMapWharf, setSelectedMapWharf] = useState<FindLocationPort | null>(null);
    const [drawnArea, setDrawnArea] = useState<Array<{ lng: number; lat: number }>>([]);
    const [areaLoading, setAreaLoading] = useState(false);
    const mapRef = useRef<ZhMapRef | null>(null);

    if (routeMmsi) return <FindLocationDetailPage loadShip={loadShip} />;
    if (!canSearch) {
        return <Empty description="暂无查船权限" />;
    }

    const search = async (searchKeyword = keyword) => {
        const value = searchKeyword.trim();
        if (!value || loading) return;
        setLoading(true);
        setErrorMessage("");
        setLastKeyword(value);
        try {
            const nextResult = (await loadShips(value)) || [];
            setResult(nextResult);
            renderShips(nextResult);
        } catch {
            setResult([]);
            setErrorMessage("查船数据加载失败");
        } finally {
            setLoading(false);
        }
    };

    /** 将 Vue ship.extent 返回的定位数据转换为 zhihao 地图船舶点位。 */
    const toMapShips = (rows: FindShipRow[]): ShipInfoVo[] =>
        rows.flatMap(row => {
            const ship = row.ship || row;
            const lon = Number(ship.lon ?? row.lon);
            const lat = Number(ship.lat ?? row.lat);
            const id = ship.id ?? row.id;
            return id !== undefined && Number.isFinite(lon) && Number.isFinite(lat)
                ? [{ id: String(id), lon, lat, name: ship.name ?? row.name, cnname: ship.cnname ?? row.cnname }]
                : [];
        });

    /** 查询后立即刷新地图船舶图层，保持 Vue 结果列表与地图点位同步。 */
    const renderShips = (rows: FindShipRow[]) => {
        const mapShips = toMapShips(rows);
        if (mapShips.length) {
            mapRef.current?.ship.render(mapShips);
        } else {
            mapRef.current?.ship.clear();
        }
    };

    /** 地图移动后使用当前视口四角调用 Vue ship.extent，并刷新地图与列表。 */
    const onMapMoveEnd = async (zoom: number) => {
        const extent = mapRef.current?.utils.getCalculateExtent();
        if (!extent || zoom < 10 || loading) return;
        const query = {
            leftTopPoint: { lng: extent[0], lat: extent[1] },
            rightTopPoint: { lng: extent[2], lat: extent[1] },
            rightBottomPoint: { lng: extent[2], lat: extent[3] },
            leftBottomPoint: { lng: extent[0], lat: extent[3] }
        };
        setLoading(true);
        setErrorMessage("");
        try {
            const nextResult = (await loadExtentShips(query)) || [];
            setResult(nextResult);
            renderShips(nextResult);
        } catch {
            setErrorMessage("地图范围船舶加载失败");
        } finally {
            setLoading(false);
        }
    };

    /** 地图点选船舶时复用 zhihao 选中态，并同步当前业务船舶标识。 */
    const onMapSelectShip = (id: string) => {
        setSelectedMapShip(id);
        const selected = toMapShips(result).find(ship => ship.id === id);
        mapRef.current?.ship.selected(selected);
    };

    /** 将 Vue 港口详情适配为 zhihao-ui-react 港口点位，并保留原港口高亮流程。 */
    const toMapPort = (port: FindLocationPort): ZhWharfEntity => ({
        id: Number(port.id),
        name: String(port.name || port.shortName || "未命名港口"),
        latLon: String(port.latLon || "")
    });

    /** 统一从地图港口/码头事件中读取业务 id，兼容组件不同版本的回调形状。 */
    const getMapFeatureId = (feature: FindLocationWharfSelection | string | number): string | number | undefined => {
        if (typeof feature === "string" || typeof feature === "number") return feature;
        if ("id" in feature && feature.id !== undefined) return feature.id;
        if ("wharfId" in feature && feature.wharfId !== undefined) return feature.wharfId;
        if ("data" in feature && feature.data && "id" in feature.data) return feature.data.id;
        return undefined;
    };

    /** 港口点选复用 Vue wharfService.getInfo，并通过 port 图层选中与地图居中反馈业务状态。 */
    const onMapSelectPort = async (port: ZhWharfEntity) => {
        const id = getMapFeatureId(port);
        if (id === undefined) return;
        try {
            const detail = await loadPort(id);
            setSelectedMapPort(detail);
            setSelectedMapWharf(null);
            const mapPort = toMapPort(detail);
            mapRef.current?.port.render([mapPort], true);
            mapRef.current?.port.selected(mapPort);
            const coordinates = parseFindLocationCoordinates(detail.latLon ? [detail.latLon] : detail.displayArea);
            const center = coordinates[0];
            if (center) {
                mapRef.current?.setCenter(center[0], center[1]);
                mapRef.current?.setZoom(DEFAULT_MAP_ZOOM);
            }
        } catch {
            setErrorMessage("港口信息加载失败");
        }
    };

    /** 码头/泊位点选复用 Vue 详情接口，并按坐标数量恢复点、线、面及高亮行为。 */
    const onMapSelectWharf = async (selection: FindLocationWharfSelection) => {
        const id = getMapFeatureId(selection);
        if (id === undefined) return;
        try {
            const detail = await loadWharf(id);
            const coordinates = parseFindLocationCoordinates(
                detail.displayArea || (detail.latLon ? [detail.latLon] : [])
            );
            const mapId = String(detail.id);
            const name = String(detail.shortName || detail.name || "未命名码头");
            setSelectedMapWharf(detail);
            setSelectedMapPort(null);
            if (coordinates.length === 1) {
                const point = { id: mapId, name, coordinate: coordinates[0] };
                mapRef.current?.wharf.createWharfPoint(point);
                mapRef.current?.wharf.selected({ type: "point", id: mapId, data: point });
                mapRef.current?.wharf.highlightPoint(mapId);
            } else if (coordinates.length === 2) {
                const line = { id: mapId, name, coordinates };
                mapRef.current?.wharf.createWharfLine(line);
                mapRef.current?.wharf.selected({ type: "line", id: mapId, data: line });
                mapRef.current?.wharf.highlightLine(mapId);
            } else if (coordinates.length > 2) {
                const polygon = { id: mapId, name, coordinates };
                mapRef.current?.wharf.createWharfPolygon(polygon);
                mapRef.current?.wharf.selected({ type: "polygon", id: mapId, data: polygon });
                mapRef.current?.wharf.highlightPolygon(mapId);
            }
            const center = coordinates.length ? coordinates[Math.floor(coordinates.length / 2)] : undefined;
            if (center) {
                mapRef.current?.setCenter(center[0], center[1]);
                mapRef.current?.setZoom(DEFAULT_MAP_ZOOM);
            }
        } catch {
            setErrorMessage("码头信息加载失败");
        }
    };

    /** 区域绘制完成后保持 Vue 的六位小数 GeoPoint 与自定义查船接口契约。 */
    const onAreaDrawEnd = async (area: number[][]) => {
        const geoPointList = toFindLocationGeoPointList(area);
        setDrawnArea(geoPointList);
        if (!geoPointList.length || areaLoading) return;
        setAreaLoading(true);
        setErrorMessage("");
        try {
            const nextResult =
                (await loadAreaShips({
                    geoPointList,
                    time: Date.now() - 10 * 60 * 1000
                })) || [];
            setResult(nextResult);
            renderShips(nextResult);
        } catch {
            setErrorMessage("区域船舶加载失败");
        } finally {
            setAreaLoading(false);
        }
    };

    return (
        <section aria-label="find-location-page" className="flex min-h-full flex-col gap-4">
            <div aria-label="查船地图" className="min-h-[360px] overflow-hidden rounded-md">
                <ZhMap
                    center={DEFAULT_MAP_CENTER}
                    className="h-full min-h-[360px] w-full"
                    ref={mapRef}
                    scaleLineUnit="nautical"
                    mapMoveEnd={zoom => void onMapMoveEnd(zoom)}
                    areaDrawEnd={area => void onAreaDrawEnd(area)}
                    selectShip={onMapSelectShip}
                    selectPort={port => void onMapSelectPort(port)}
                    selectWharf={wharf => void onMapSelectWharf(wharf)}
                    showGreenLayer
                    showScale
                    zoom={DEFAULT_MAP_ZOOM}
                />
            </div>
            <span aria-label="地图已选船舶" className="sr-only">
                {selectedMapShip}
            </span>
            <span aria-label="地图已选港口" className="sr-only">
                {selectedMapPort?.name || ""}
            </span>
            <span aria-label="地图已选码头" className="sr-only">
                {selectedMapWharf?.name || ""}
            </span>
            <span aria-label="地图区域" className="sr-only">
                {drawnArea.length ? `${drawnArea.length} 个点` : "未绘制"}
            </span>
            <Card>
                <Space align="center" wrap>
                    <Typography.Title className="mb-0" level={4}>
                        查船
                    </Typography.Title>
                    <Tag>ROLE_LF_SHIP</Tag>
                </Space>
            </Card>
            <Card>
                <Space.Compact className="w-full">
                    <Input
                        aria-label="船舶名称或MMSI"
                        allowClear
                        placeholder="请输入船舶名称或MMSI"
                        value={keyword}
                        onChange={event => {
                            const value = event.target.value;
                            setKeyword(value);
                            // 对齐 Vue clearSearchValue：清空输入后退出旧结果态，避免继续展示过期船舶。
                            if (!value) {
                                setResult([]);
                                setLastKeyword("");
                                setErrorMessage("");
                                setSelectedMapShip("");
                                mapRef.current?.ship.clear();
                            }
                        }}
                        onPressEnter={() => void search()}
                    />
                    <Button aria-label="查询" type="primary" loading={loading} onClick={() => void search()}>
                        查询
                    </Button>
                    <Button
                        aria-label="刷新"
                        disabled={!lastKeyword || loading}
                        onClick={() => void search(lastKeyword)}
                    >
                        刷新
                    </Button>
                </Space.Compact>
                {errorMessage ? <Alert className="mt-4" message={errorMessage} role="alert" type="error" /> : null}
                {loading || areaLoading ? <Spin className="mt-4" aria-label="查船数据加载中" /> : null}
                {!loading && !areaLoading && !errorMessage && lastKeyword && !result.length ? (
                    <Empty className="mt-4" description="暂无数据" />
                ) : null}
                <div className="mt-4 flex flex-col gap-2">
                    {result.map(row => {
                        const ship = normalizeFindShipRow(row);
                        return (
                            <Card key={ship.mmsi} size="small">
                                <Space align="center" className="w-full" wrap>
                                    <Typography.Text strong>{ship.name}</Typography.Text>
                                    {ship.englishName ? <Typography.Text>{ship.englishName}</Typography.Text> : null}
                                    <Typography.Text>{ship.mmsi}</Typography.Text>
                                    <Button
                                        aria-label="查看位置"
                                        type="link"
                                        onClick={() =>
                                            navigate(`/logistics/findLocation/${encodeURIComponent(ship.mmsi)}`)
                                        }
                                    >
                                        查看位置
                                    </Button>
                                </Space>
                            </Card>
                        );
                    })}
                </div>
            </Card>
        </section>
    );
}

export default FindLocationPage;
